import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  Mail,
  Package,
  Star,
  Sparkles,
  Copy,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import SEO from "@/components/SEO";
import {
  adminMe,
  setAdminToken,
  getAdminToken,
  adminSubmissionsCounts,
  adminListContacts,
  adminUpdateContact,
  adminDeleteContact,
  adminReclassifyContact,
  adminListProductRequests,
  adminUpdateProductRequest,
  adminDeleteProductRequest,
  adminListTestimonials,
  adminUpdateTestimonial,
  adminDeleteTestimonial,
} from "@/lib/api";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

const TABS = [
  { key: "contact", label: "Contact", icon: Mail, statuses: ["New", "Read", "Responded", "Archived"] },
  { key: "product", label: "Product Requests", icon: Package, statuses: ["Open", "Contacted", "Fulfilled", "Closed"] },
  { key: "testimonials", label: "Testimonials", icon: Star, statuses: ["Pending", "Approved", "Rejected"] },
];

export default function AdminSubmissions() {
  const nav = useNavigate();
  const [tab, setTab] = useState("contact");
  const [statusFilter, setStatusFilter] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    if (!getAdminToken()) { nav("/admin/login", { replace: true }); return; }
    adminMe().then((res) => { if (res?.expires_at) setAdminToken(getAdminToken(), res.expires_at); })
      .catch(() => { setAdminToken(""); nav("/admin/login", { replace: true }); });
  }, [nav]);

  useSessionRenewal({ onExpired: () => { setAdminToken(""); nav("/admin/login", { replace: true }); } });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const fns = {
        contact: adminListContacts,
        product: adminListProductRequests,
        testimonials: adminListTestimonials,
      };
      const data = await fns[tab](statusFilter || undefined);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("load failed", err);
      toast.error("Could not load submissions.");
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  const loadCounts = useCallback(async () => {
    try {
      const c = await adminSubmissionsCounts();
      setCounts(c || {});
    } catch (err) {
      console.warn("counts failed", err);
    }
  }, []);

  useEffect(() => { setStatusFilter(""); }, [tab]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadCounts(); }, [loadCounts, tab]);

  const updateStatus = async (id, newStatus) => {
    setBusy((b) => ({ ...b, [id]: newStatus }));
    try {
      const fns = {
        contact: adminUpdateContact,
        product: adminUpdateProductRequest,
        testimonials: adminUpdateTestimonial,
      };
      await fns[tab](id, { status: newStatus });
      toast.success(`Marked as ${newStatus}.`);
      await loadList();
      await loadCounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    setBusy((b) => ({ ...b, [id]: "delete" }));
    try {
      const fns = {
        contact: adminDeleteContact,
        product: adminDeleteProductRequest,
        testimonials: adminDeleteTestimonial,
      };
      await fns[tab](id);
      toast.success("Deleted.");
      await loadList();
      await loadCounts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  const activeTab = TABS.find((t) => t.key === tab);
  const tabCounts = counts[tab === "product" ? "product_requests" : tab] || {};

  return (
    <main data-testid="admin-submissions-page" className="bg-white min-h-screen">
      <SEO title="Submissions" path="/admin/submissions" noIndex />

      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="ash-eyebrow">Ashlan Admin</span>
            <h1 className="text-2xl md:text-3xl font-bold text-[#080808]">Submissions</h1>
          </div>
          <Link to="/admin/blog" className="text-sm text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Blog console
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex gap-2 border-b border-[#e5e7eb] mb-6 flex-wrap" data-testid="tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const total = (counts[t.key === "product" ? "product_requests" : t.key] || {}).total || 0;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors ${
                  tab === t.key
                    ? "text-[#3A7A8C] border-b-2 border-[#3A7A8C] -mb-px"
                    : "text-[#5A5F6A] hover:text-[#080808]"
                }`}
              >
                <Icon size={14} /> {t.label}
                <span className="text-xs opacity-60">({total})</span>
              </button>
            );
          })}
        </div>

        {/* Status counts strip */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filter">
          <button
            onClick={() => setStatusFilter("")}
            data-testid="status-all"
            className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold border ${
              !statusFilter ? "bg-[#080808] text-white border-[#080808]" : "bg-white text-[#3C4048] border-[#e5e7eb] hover:bg-[#F5F5F5]"
            }`}
          >
            All <span className="opacity-70">({tabCounts.total || 0})</span>
          </button>
          {activeTab.statuses.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`status-${s.toLowerCase()}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-semibold border ${
                statusFilter === s
                  ? "bg-[#3A7A8C] text-white border-[#3A7A8C]"
                  : "bg-white text-[#3C4048] border-[#e5e7eb] hover:bg-[#F5F5F5]"
              }`}
            >
              {s} <span className="opacity-70">({tabCounts[s] || 0})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[#5A5F6A]"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#e5e7eb] p-12 text-center text-[#5A5F6A]" data-testid="empty-state">
            No {tab} submissions{statusFilter ? ` with status "${statusFilter}"` : ""}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {items.map((it) => (
              <SubmissionCard
                key={it.id}
                item={it}
                tab={tab}
                statuses={activeTab.statuses}
                busy={busy[it.id]}
                onStatusChange={updateStatus}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function SubmissionCard({ item, tab, statuses, busy, onStatusChange, onDelete }) {
  return (
    <div className="border border-[#e5e7eb] bg-white p-5" data-testid={`submission-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-semibold text-[#080808]">
            {item.name}
            {tab === "product" && <span className="text-[#5A5F6A] font-normal"> · {item.product_name}</span>}
            {tab === "testimonials" && item.location && (
              <span className="text-[#5A5F6A] font-normal"> · {item.location}</span>
            )}
          </div>
          <div className="text-xs text-[#5A5F6A] mt-0.5">
            {item.email}
            {item.phone && <> · {item.phone}</>}
            {item.subject && <> · {item.subject}</>}
            {" · "}
            {item.created_at?.slice(0, 16).replace("T", " ")}
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1 border bg-[#F5F5F5] text-[#3C4048] border-[#e5e7eb]"
          data-testid={`status-pill-${item.id}`}
        >
          {item.status || "—"}
        </span>
      </div>
      <p className="text-sm text-[#3C4048] whitespace-pre-wrap leading-relaxed">
        {item.message || item.quote}
      </p>
      {tab === "contact" && <OfficeAssistantPanel item={item} />}
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {statuses.filter((s) => s !== item.status).map((s) => (
          <button
            key={s}
            onClick={() => onStatusChange(item.id, s)}
            disabled={!!busy}
            className="ash-btn-outline inline-flex items-center gap-1.5 text-xs px-3 py-2 disabled:opacity-50"
            data-testid={`set-${s.toLowerCase()}-${item.id}`}
          >
            {busy === s ? <Loader2 size={11} className="animate-spin" /> : (
              s === "Approved" || s === "Responded" || s === "Fulfilled"
                ? <CheckCircle2 size={11} />
                : s === "Rejected" || s === "Closed" || s === "Archived"
                ? <XCircle size={11} />
                : null
            )}
            Mark {s}
          </button>
        ))}
        <button
          onClick={() => onDelete(item.id)}
          disabled={!!busy}
          className="ml-auto text-xs text-rose-700 hover:text-rose-900 inline-flex items-center gap-1.5 px-3 py-2 disabled:opacity-50"
          data-testid={`delete-${item.id}`}
        >
          {busy === "delete" ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          Delete
        </button>
      </div>
    </div>
  );
}

const CATEGORY_COLORS = {
  booking: "#3A7A8C",
  billing: "#D97706",
  insurance: "#7C3AED",
  clinical: "#DC2626",
  partnership: "#059669",
  complaint: "#DC2626",
  press: "#0891B2",
  spam: "#6B7280",
  general: "#64748B",
};

function OfficeAssistantPanel({ item }) {
  const [reply, setReply] = useState(item.ai_suggested_reply || "");
  const [classifying, setClassifying] = useState(false);
  const [aiData, setAiData] = useState({
    category: item.ai_category,
    urgency: item.ai_urgency,
    suggested_reply: item.ai_suggested_reply,
  });
  const hasClassification = !!aiData.category;

  const reclassify = async () => {
    setClassifying(true);
    try {
      const res = await adminReclassifyContact(item.id);
      setAiData(res);
      setReply(res.suggested_reply || "");
      toast.success("Classification refreshed.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not classify.");
    } finally {
      setClassifying(false);
    }
  };

  const copyReply = async () => {
    if (!reply.trim()) return;
    try {
      await navigator.clipboard.writeText(reply);
      toast.success("Reply copied — paste into Gmail.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const openMailto = () => {
    const subj = item.subject ? `Re: ${item.subject}` : "Re: your message to Ashlan Clinic";
    const body = reply || "";
    const url = `mailto:${encodeURIComponent(item.email)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <div className="mt-4 bg-[#F9FAFB] border border-[#E5E7EB] p-4" data-testid={`ai-panel-${item.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles size={14} className="text-[#3A7A8C]" />
          <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#3A7A8C]">Office Assistant</span>
          {hasClassification && (
            <>
              <span
                className="text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 text-white"
                style={{ background: CATEGORY_COLORS[aiData.category] || "#64748B" }}
                data-testid={`ai-category-${item.id}`}
              >
                {aiData.category}
              </span>
              {aiData.urgency === "high" && (
                <span className="text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] inline-flex items-center gap-1" data-testid={`ai-urgency-${item.id}`}>
                  <AlertTriangle size={10} /> High urgency
                </span>
              )}
              {aiData.urgency === "low" && (
                <span className="text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 bg-[#F3F4F6] text-[#6B7280]" data-testid={`ai-urgency-${item.id}`}>
                  Low urgency
                </span>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={reclassify}
          disabled={classifying}
          className="text-[11px] text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid={`ai-reclassify-${item.id}`}
        >
          {classifying ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {hasClassification ? "Re-classify" : "Run classifier"}
        </button>
      </div>

      {hasClassification ? (
        <>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={6}
            placeholder="(The Office Assistant did not draft a reply — likely spam or press. Write your own here or ignore.)"
            className="w-full bg-white border border-[#E5E7EB] p-3 text-sm text-[#080808] leading-relaxed focus:border-[#3A7A8C] focus:outline-none"
            data-testid={`ai-reply-${item.id}`}
          />
          <p className="mt-1.5 text-[10px] text-[#6B7280] italic">
            Drafted by AI — always review for tone, accuracy, and Ashlan voice before sending. No outbound is sent automatically.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openMailto}
              disabled={!reply.trim()}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#3A7A8C] text-white font-bold uppercase tracking-[0.12em] text-[11px] hover:bg-[#2E6573] disabled:opacity-50"
              data-testid={`ai-open-mail-${item.id}`}
            >
              <Mail size={12} /> Open in Gmail
            </button>
            <button
              type="button"
              onClick={copyReply}
              disabled={!reply.trim()}
              className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E5E7EB] text-[#080808] font-bold uppercase tracking-[0.12em] text-[11px] hover:border-[#3A7A8C] disabled:opacity-50"
              data-testid={`ai-copy-${item.id}`}
            >
              <Copy size={12} /> Copy reply
            </button>
          </div>
        </>
      ) : (
        <p className="text-xs text-[#6B7280]">
          No AI classification yet for this message — click <strong>Run classifier</strong> above to generate one.
        </p>
      )}
    </div>
  );
}
